import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { CompletedStep } from '../components/job-simulations/CompletedStep';
import { FinalResultDialog } from '../components/job-simulations/FinalResultDialog';
import { ResultSummary } from '../components/common/ResultSummary';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { useContentProtection } from '../hooks/useContentProtection';
import { VoiceWaveform } from '../components/ai/VoiceWaveform';
import { SessionGuidanceModal } from '../components/training/SessionGuidanceModal';
import { QualityTaskOutcome } from '../components/quality/QualityTaskOutcome';
import { 
    ArrowLeft,
    BookOpen,
    Play,
    Pause,
    Volume2,
    VolumeX,
    CheckCircle,
    XCircle,
    ChevronRight,
    ChevronLeft,
    Target,
    Users,
    MessageSquare,
    Award,
    Loader2,
    Lock,
    Clock,
    Star,
    FileText,
    Mic,
    MicOff,
    RefreshCw,
    Sparkles,
    TrendingUp,
    AlertCircle,
    Trophy,
    AlertTriangle,
    Info,
    Briefcase
} from 'lucide-react';

// Phase 5 Constants
const PASSING_SCORE = 80;
const FINAL_ASSESSMENT_VERDICT_AT = 3;
const FINAL_ASSESSMENT_MAX_EXCHANGES = 10;

const difficultyColors = {
    beginner: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    intermediate: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    advanced: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    expert: 'bg-red-500/10 text-red-600 border-red-500/30'
};

const SkillLearning = () => {
    const { skillId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const lessonFromUrl = searchParams.get('lesson');
    const assignedLesson = Boolean(searchParams.get('qt') && lessonFromUrl);
    const { api } = useAuth();
    const audioRef = useRef(null);
    const ttsAudioRef = useRef(null);
    
    // Enable content protection (disable right-click, copy, screenshots)
    useContentProtection(true);
    
    const [skill, setSkill] = useState(null);
    const [finalResult, setFinalResult] = useState(null);
    const [playbook, setPlaybook] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('lessons');
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [quizResult, setQuizResult] = useState(null);
    const [submittingQuiz, setSubmittingQuiz] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [generatingVoice, setGeneratingVoice] = useState(false);
    
    // Simulation states - Voice-Only Mode
    const [activeSimulation, setActiveSimulation] = useState(null);
    useProctoredAttempt({
        active: Boolean(activeSimulation) || (showQuiz && !quizResult),
        label: activeSimulation ? 'simulation' : 'quiz',
        onForceEnd: () => (activeSimulation ? completeSimulation() : undefined),
    });
    const [simMessages, setSimMessages] = useState([]);
    const [simLoading, setSimLoading] = useState(false);
    const [simCompleting, setSimCompleting] = useState(false);
    const [selectedVerdict, setSelectedVerdict] = useState(null);
    const [verdictReasoning, setVerdictReasoning] = useState('');
    
    // Voice recording states
    const [isRecording, setIsRecording] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [userExchanges, setUserExchanges] = useState(0);
    const [shouldPromptVerdict, setShouldPromptVerdict] = useState(false);
    const [verdictRequired, setVerdictRequired] = useState(false);
    
    // Pre-session guidance modal states
    const [showGuidanceModal, setShowGuidanceModal] = useState(false);
    const [pendingSimulationType, setPendingSimulationType] = useState(null);
    
    // Naturalness detection warning
    const [naturalnessWarning, setNaturalnessWarning] = useState(null);
    
    // Audio refs for voice recording
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const messagesEndRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const streamRef = useRef(null);

    const fetchSkillData = useCallback(async (preserveCurrentLesson = false) => {
        try {
            const [skillRes, progressRes] = await Promise.all([
                api.get(`/job-simulations/skills/${skillId}`),
                api.get(`/job-simulations/skills/${skillId}/progress`)
            ]);
            
            setSkill(skillRes.data);
            setPlaybook(skillRes.data.playbook);
            setProgress(progressRes.data);
            
            // Only auto-set current lesson on initial load, not when user has explicitly selected a lesson
            if (!preserveCurrentLesson && progressRes.data?.lesson_progress?.length > 0) {
                const lessons = skillRes.data.playbook?.lessons || [];
                // Find the first lesson that hasn't been passed by matching lesson IDs
                const firstUnpassedIndex = lessons.findIndex(lesson => {
                    const lessonProg = progressRes.data.lesson_progress.find(lp => lp.lesson_id === lesson.id);
                    return !lessonProg?.quiz_passed;
                });
                if (firstUnpassedIndex >= 0) {
                    setCurrentLessonIndex(firstUnpassedIndex);
                }
            }
            // Quality Task deep link: open the assigned lesson
            if (!preserveCurrentLesson && lessonFromUrl) {
                const assignedIndex = (skillRes.data.playbook?.lessons || []).findIndex(lesson => lesson.id === lessonFromUrl);
                if (assignedIndex >= 0) setCurrentLessonIndex(assignedIndex);
            }
        } catch (error) {
            toast.error('Failed to load skill data');
            navigate('/job-simulations');
        } finally {
            setLoading(false);
        }
    }, [api, skillId, lessonFromUrl, navigate]);

    useEffect(() => {
        fetchSkillData();
    }, [fetchSkillData]);

    const startLesson = async (lessonId) => {
        try {
            await api.post(`/job-simulations/skills/${skillId}/lessons/${lessonId}/start`);
            // Preserve current lesson when refreshing data after starting a lesson
            await fetchSkillData(true);
        } catch (error) {
            console.error('Error starting lesson:', error);
        }
    };

    const generateVoice = async (lessonId) => {
        setGeneratingVoice(true);
        try {
            const response = await api.post(`/job-simulations/skills/${skillId}/lessons/${lessonId}/generate-voice`);
            
            if (!response.data.voice_url) {
                // Fallback to browser TTS if no URL received
                const currentLesson = playbook?.lessons?.find(l => l.id === lessonId);
                if (currentLesson?.content) {
                    toast.info('Using browser voice. Enable Lemonfox API for premium voice.');
                    speakWithBrowserTTS(currentLesson.content);
                } else {
                    toast.error('No voice content available');
                }
                return;
            }
            
            // Update playbook with voice URL
            const updatedLessons = playbook.lessons.map(l => 
                l.id === lessonId ? { ...l, voice_url: response.data.voice_url } : l
            );
            setPlaybook({ ...playbook, lessons: updatedLessons });
            
            // Play the audio with timeout
            if (audioRef.current) {
                audioRef.current.src = response.data.voice_url;
                
                // Set timeout for audio loading
                const loadTimeout = setTimeout(() => {
                    if (!audioRef.current.readyState || audioRef.current.readyState < 2) {
                        console.warn('Audio load timeout, falling back to browser TTS');
                        const currentLesson = playbook?.lessons?.find(l => l.id === lessonId);
                        if (currentLesson?.content) {
                            speakWithBrowserTTS(currentLesson.content);
                        }
                    }
                }, 10000); // 10 second timeout
                
                // Add error handler for audio loading
                audioRef.current.onerror = (e) => {
                    clearTimeout(loadTimeout);
                    console.error('Audio playback error:', e);
                    // Fallback to browser TTS
                    const currentLesson = playbook?.lessons?.find(l => l.id === lessonId);
                    if (currentLesson?.content) {
                        toast.info('Audio failed. Using browser voice instead.');
                        speakWithBrowserTTS(currentLesson.content);
                    } else {
                        toast.error('Could not play audio. Please try again.');
                    }
                    setIsPlaying(false);
                };
                
                audioRef.current.oncanplaythrough = () => {
                    clearTimeout(loadTimeout);
                };
                
                try {
                    await audioRef.current.play();
                    setIsPlaying(true);
                } catch (playError) {
                    clearTimeout(loadTimeout);
                    console.error('Audio play error:', playError);
                    // Fallback to browser TTS
                    const currentLesson = playbook?.lessons?.find(l => l.id === lessonId);
                    if (currentLesson?.content) {
                        toast.info('Audio failed. Using browser voice instead.');
                        speakWithBrowserTTS(currentLesson.content);
                    } else {
                        toast.error('Could not play audio. Please click Listen again.');
                    }
                }
            }
        } catch (error) {
            console.error('Voice generation error:', error);
            const errorMsg = error.response?.data?.detail || 'Failed to generate voice';
            // Fallback to browser TTS on API error
            const currentLesson = playbook?.lessons?.find(l => l.id === lessonId);
            if (currentLesson?.content) {
                toast.info('Voice service unavailable. Using browser voice instead.');
                speakWithBrowserTTS(currentLesson.content);
            } else {
                toast.error(errorMsg);
            }
        } finally {
            setGeneratingVoice(false);
        }
    };
    
    // Browser TTS fallback function
    const speakWithBrowserTTS = (text) => {
        if ('speechSynthesis' in window) {
            // Stop any current speech
            window.speechSynthesis.cancel();
            
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            
            utterance.onstart = () => setIsPlaying(true);
            utterance.onend = () => setIsPlaying(false);
            utterance.onerror = () => {
                setIsPlaying(false);
                toast.error('Browser voice failed. Please try again.');
            };
            
            window.speechSynthesis.speak(utterance);
        } else {
            toast.error('Voice playback not supported in this browser');
        }
    };

    const submitQuiz = async () => {
        const currentLesson = playbook?.lessons?.[currentLessonIndex];
        if (!currentLesson) return;
        
        // Check if quiz questions exist
        if (!currentLesson.quiz_questions || currentLesson.quiz_questions.length === 0) {
            toast.error('No quiz questions available. Please refresh and try again.');
            return;
        }
        
        // Convert quiz answers to array, checking for both numeric and undefined values
        const answers = currentLesson.quiz_questions.map((_, idx) => {
            const answer = quizAnswers[idx];
            // Check if answer exists and is a valid number
            if (answer === undefined || answer === null || answer === '') {
                return -1;
            }
            // Handle both string and number values from RadioGroup
            const numAnswer = typeof answer === 'string' ? parseInt(answer, 10) : answer;
            return isNaN(numAnswer) ? -1 : numAnswer;
        });
        
        // Check if any question is unanswered
        const unansweredCount = answers.filter(a => a === -1).length;
        if (unansweredCount > 0) {
            toast.error(`Please answer all questions (${unansweredCount} unanswered)`);
            return;
        }
        
        setSubmittingQuiz(true);
        try {
            const response = await api.post(
                `/job-simulations/skills/${skillId}/lessons/${currentLesson.id}/quiz`,
                { answers }
            );
            
            setQuizResult(response.data);
            
            if (response.data.passed) {
                toast.success('Quiz passed! Great job!');
                // Preserve current lesson when refreshing after quiz pass
                await fetchSkillData(true);
            } else {
                toast.error(`Quiz not passed. Score: ${response.data.score}%`);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Failed to submit quiz';
            // Handle max attempts error - offer to retry with new questions
            if (errorMessage.includes('Maximum attempts')) {
                toast.error('Maximum attempts reached. Click "Retry Quiz" to get new questions.');
                setQuizResult({ 
                    maxAttemptsReached: true, 
                    message: 'Maximum attempts reached. New questions will be generated on retry.'
                });
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setSubmittingQuiz(false);
        }
    };

    const nextLesson = () => {
        if (currentLessonIndex < (playbook?.lessons?.length || 0) - 1) {
            setCurrentLessonIndex(currentLessonIndex + 1);
            setShowQuiz(false);
            setQuizAnswers({});
            setQuizResult(null);
        }
    };

    const prevLesson = () => {
        if (currentLessonIndex > 0) {
            setCurrentLessonIndex(currentLessonIndex - 1);
            setShowQuiz(false);
            setQuizAnswers({});
            setQuizResult(null);
        }
    };

    // Handler to show guidance modal before starting simulation
    const handleStartSimulation = (type) => {
        setPendingSimulationType(type);
        setShowGuidanceModal(true);
    };

    // Actually start the simulation after guidance is dismissed
    const startSimulation = async (type) => {
        setSimLoading(true);
        setUserExchanges(0);
        setShouldPromptVerdict(false);
        setVerdictRequired(false);
        setNaturalnessWarning(null);
        try {
            let response;
            if (type === 'job_analysis') {
                response = await api.post(`/job-simulations/skills/${skillId}/start-job-analysis`);
            } else if (type === 'candidate_screening') {
                response = await api.post(`/job-simulations/skills/${skillId}/start-screening`);
            } else if (type === 'final_assessment') {
                response = await api.post(`/job-simulations/skills/${skillId}/start-final-assessment`);
            }
            
            setActiveSimulation(response.data);
            setSimMessages(response.data.messages || []);
            setActiveTab('simulation');
            
            // Auto-speak the opening message (TTS)
            const openingMessage = response.data.messages?.[0]?.content;
            if (openingMessage && !isMuted) {
                speakText(openingMessage);
            }
            
            toast.success(`${type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} started!`, {
                description: 'Voice-only mode enabled. Click the mic to start recording.'
            });
            
            // Auto-start microphone after a short delay for user to prepare
            setTimeout(() => {
                if (!isMuted && !isSpeaking) {
                    // Don't auto-start, but prompt user to click mic
                    toast.info('Click the microphone to speak', { duration: 3000 });
                }
            }, 2000);
            
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start simulation');
        } finally {
            setSimLoading(false);
        }
    };

    // TTS using browser API  
    const speakText = (text) => {
        if (!text || isMuted) return;
        
        window.speechSynthesis?.cancel();
        setIsSpeaking(true);
        
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeaking = () => {
        window.speechSynthesis?.cancel();
        setIsSpeaking(false);
    };

    // Start voice recording with audio level monitoring
    const startRecording = async () => {
        try {
            stopSpeaking();
            
            // Check if MediaRecorder is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                toast.error('Voice recording is not supported in this browser. Please use Chrome or Firefox.');
                return;
            }
            
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            streamRef.current = stream;
            
            // Set up audio context for level monitoring
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);
            analyserRef.current.fftSize = 256;
            
            // Monitor audio level
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            const updateLevel = () => {
                if (analyserRef.current) {
                    analyserRef.current.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                    setAudioLevel(average / 128);
                    animationFrameRef.current = requestAnimationFrame(updateLevel);
                }
            };
            updateLevel();
            
            // Set up media recorder with fallback MIME types
            let mimeType = 'audio/webm';
            if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                mimeType = 'audio/webm;codecs=opus';
            } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                mimeType = 'audio/webm';
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                mimeType = 'audio/mp4';
            } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
                mimeType = 'audio/ogg';
            }
            
            try {
                mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
            } catch (recorderErr) {
                console.warn('MediaRecorder with mimeType failed, using default:', recorderErr);
                mediaRecorderRef.current = new MediaRecorder(stream);
            }
            
            audioChunksRef.current = [];
            
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };
            
            mediaRecorderRef.current.onerror = (e) => {
                console.error('MediaRecorder error:', e);
                toast.error('Recording failed. Please try again.');
                setIsRecording(false);
                stream.getTracks().forEach(track => track.stop());
            };
            
            mediaRecorderRef.current.onstop = async () => {
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                setAudioLevel(0);
                
                // Check if we have audio data
                if (audioChunksRef.current.length === 0) {
                    toast.error('No audio recorded. Please speak into the microphone.');
                    stream.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                    return;
                }
                
                const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorderRef.current?.mimeType || 'audio/webm' });
                
                // Check blob size
                if (audioBlob.size < 1000) {
                    toast.error('Recording too short. Please speak longer.');
                    stream.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                    return;
                }
                
                await sendVoiceMessage(audioBlob);
                
                stream.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            };
            
            mediaRecorderRef.current.start();
            setIsRecording(true);
            toast.info('Recording...', { description: 'Speak clearly, then click the mic button to stop' });
            
        } catch (err) {
            console.error('Failed to start recording:', err);
            
            // Provide specific error messages based on the error type
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                toast.error('Microphone access denied', {
                    description: 'Please allow microphone access in your browser settings and refresh the page.',
                    duration: 5000
                });
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                toast.error('No microphone found', {
                    description: 'Please connect a microphone and refresh the page.',
                    duration: 5000
                });
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                toast.error('Microphone is busy', {
                    description: 'Please close other apps using the microphone and try again.',
                    duration: 5000
                });
            } else {
                toast.error('Could not access microphone', {
                    description: `Error: ${err.message || 'Unknown error'}. Please try refreshing the page.`,
                    duration: 5000
                });
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        }
    };

    // Send voice message - transcribed by backend with TTS response
    const sendVoiceMessage = async (audioBlob) => {
        if (!activeSimulation?.id) return;
        
        setSimLoading(true);
        
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            
            const response = await api.post(
                `/job-simulations/simulations/${activeSimulation.id}/voice`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            
            // Add transcribed message to chat
            if (response.data.transcript) {
                setSimMessages(prev => [...prev, {
                    role: 'user',
                    content: response.data.transcript,
                    isVoice: true
                }]);
            }
            
            // Add AI response
            setSimMessages(prev => [...prev, {
                role: 'assistant',
                content: response.data.response
            }]);
            
            // Update exchange tracking
            if (response.data.user_exchanges !== undefined) {
                setUserExchanges(response.data.user_exchanges);
            }
            if (response.data.should_prompt_verdict) {
                setShouldPromptVerdict(true);
            }
            if (response.data.verdict_required) {
                setVerdictRequired(true);
            }
            
            // Handle naturalness detection warning (soft warning only)
            if (response.data.naturalness_warning) {
                setNaturalnessWarning(response.data.naturalness_warning);
                toast.warning('Response flagged for review', {
                    description: 'Please speak naturally from your own knowledge.',
                    duration: 5000
                });
            }
            
            // Speak the response (TTS) - prefer server-generated audio, fallback to browser TTS
            if (response.data.voice_url && ttsAudioRef.current) {
                setIsSpeaking(true);
                ttsAudioRef.current.src = response.data.voice_url;
                
                // Set up event handlers for TTS audio
                ttsAudioRef.current.onplay = () => setIsSpeaking(true);
                ttsAudioRef.current.onended = () => setIsSpeaking(false);
                ttsAudioRef.current.onerror = (e) => {
                    console.error('TTS audio error:', e);
                    setIsSpeaking(false);
                    // Fallback to browser TTS on error
                    if (!isMuted) {
                        toast.info('Using browser voice', { duration: 2000 });
                        speakText(response.data.response);
                    }
                };
                
                try {
                    await ttsAudioRef.current.play();
                } catch (playError) {
                    console.warn('Audio autoplay blocked, trying with user interaction:', playError);
                    setIsSpeaking(false);
                    // Fallback to browser TTS if autoplay blocked
                    if (!isMuted) {
                        speakText(response.data.response);
                    }
                }
            } else if (!isMuted && response.data.response) {
                speakText(response.data.response);
            }
            
        } catch (err) {
            console.error('Failed to process voice:', err);
            const errorMsg = err.response?.data?.detail || 'Failed to process your voice. Please try again.';
            toast.error(errorMsg);
        } finally {
            setSimLoading(false);
        }
    };

    const toggleMute = () => {
        if (!isMuted) {
            stopSpeaking();
        }
        setIsMuted(!isMuted);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            window.speechSynthesis?.cancel();
        };
    }, []);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [simMessages]);

    const completeSimulation = async () => {
        if (!activeSimulation) return;
        
        setSimCompleting(true);
        stopSpeaking();
        try {
            const response = await api.post(`/job-simulations/simulations/${activeSimulation.id}/complete`);
            
            const score = response.data.scores?.overall || 0;
            const best = response.data.best_score ?? score;
            const bestNote = best > score ? ` (your best is still ${best}%)` : '';
            
            if (score >= PASSING_SCORE) {
                toast.success(`Simulation completed! Score: ${score}%${bestNote}`);
            } else {
                toast.warning(`Score: ${score}%${bestNote}. You can retake it any time to improve.`);
            }
            
            setActiveSimulation(null);
            setSimMessages([]);
            setUserExchanges(0);
            setShouldPromptVerdict(false);
            setVerdictRequired(false);
            await fetchSkillData();
            setActiveTab('lessons');
        } catch (error) {
            toast.error('Failed to complete simulation');
        } finally {
            setSimCompleting(false);
        }
    };

    const submitVerdict = async () => {
        if (!selectedVerdict || !verdictReasoning.trim()) {
            toast.error('Please select a verdict and provide reasoning');
            return;
        }
        
        setSimCompleting(true);
        stopSpeaking();
        try {
            const response = await api.post(
                `/job-simulations/simulations/${activeSimulation.id}/submit-verdict`,
                { verdict: selectedVerdict, reasoning: verdictReasoning }
            );
            
            const score = response.data.scores?.overall || 0;
            const passed = response.data.passed;
            
            if (passed) {
                toast.success(`PASSED! Score: ${score}%`, {
                    description: `Jobs completed: ${response.data.phase_5_progress?.jobs_completed}/${response.data.phase_5_progress?.jobs_required}`
                });
            } else {
                toast.warning(`Score: ${score}% - Need ${PASSING_SCORE}% to pass`, {
                    description: 'Retry this job to advance to the next one.'
                });
            }
            setFinalResult({
                skillName: skill?.name,
                score,
                passed,
                verdict: selectedVerdict,
                verdictCorrect: response.data.verdict_correct,
                passMark: PASSING_SCORE,
                overallSkillScore: response.data.overall_skill_score,
                jobsCompleted: response.data.phase_5_progress?.jobs_completed,
                jobsRequired: response.data.phase_5_progress?.jobs_required,
                feedback: response.data.feedback,
                completedAt: new Date().toISOString(),
            });
            
            setActiveSimulation(null);
            setSimMessages([]);
            setSelectedVerdict(null);
            setVerdictReasoning('');
            setUserExchanges(0);
            setShouldPromptVerdict(false);
            setVerdictRequired(false);
            await fetchSkillData();
            setActiveTab('lessons');
        } catch (error) {
            toast.error('Failed to submit verdict');
        } finally {
            setSimCompleting(false);
        }
    };

    const currentLesson = playbook?.lessons?.[currentLessonIndex];
    const lessonProgress = progress?.lesson_progress?.find(lp => lp.lesson_id === currentLesson?.id);
    const allLessonsCompleted = progress?.lessons_completed >= progress?.total_lessons;

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    if (!skill) {
        return (
            <MainLayout>
                <div className="text-center py-12">
                    <p className="text-muted-foreground">Skill not found</p>
                    <Button onClick={() => navigate('/job-simulations')} className="mt-4">
                        Back to Skills
                    </Button>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="skill-learning-page">
                {/* Header */}
                <div className="flex items-start gap-4">
                    {!assignedLesson && (
                        <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate('/job-simulations')}
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    )}
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-2xl font-bold">{skill.name}</h1>
                            <Badge variant="outline" className={difficultyColors[skill.difficulty_base]}>
                                {skill.difficulty_base}
                            </Badge>
                            {progress?.overall_status === 'completed' && (
                                <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                                    <Award className="h-3 w-3 mr-1" />
                                    Completed
                                </Badge>
                            )}
                        </div>
                        <p className="text-muted-foreground">{skill.description}</p>
                    </div>
                </div>

                {/* Progress Overview */}
                {!assignedLesson && <Card>
                    <CardContent className="p-4">
                        <div className="grid grid-cols-5 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                    {progress?.lessons_completed || 0}/{progress?.total_lessons || 0}
                                </div>
                                <p className="text-xs text-muted-foreground">Lessons</p>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${progress?.job_analysis_completed ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                                    {progress?.job_analysis_completed ? <CheckCircle className="h-6 w-6 mx-auto" /> : <Lock className="h-6 w-6 mx-auto" />}
                                </div>
                                <p className="text-xs text-muted-foreground">Job Analysis</p>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${progress?.candidate_screening_completed ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                                    {progress?.candidate_screening_completed ? <CheckCircle className="h-6 w-6 mx-auto" /> : <Lock className="h-6 w-6 mx-auto" />}
                                </div>
                                <p className="text-xs text-muted-foreground">Screening</p>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${progress?.final_assessment_completed ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                                    {progress?.final_assessment_completed ? <CheckCircle className="h-6 w-6 mx-auto" /> : <Lock className="h-6 w-6 mx-auto" />}
                                </div>
                                <p className="text-xs text-muted-foreground">Assessment</p>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-amber-500">
                                    {progress?.overall_score || '-'}%
                                </div>
                                <p className="text-xs text-muted-foreground">Overall Score</p>
                            </div>
                        </div>
                        <Progress 
                            value={((progress?.lessons_completed || 0) / Math.max(progress?.total_lessons || 1, 1)) * 100} 
                            className="h-2 mt-4"
                        />
                    </CardContent>
                </Card>}

                {/* Main Content */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className={assignedLesson ? 'hidden' : 'grid w-full grid-cols-3'}>
                        <TabsTrigger value="lessons" className="gap-2">
                            <BookOpen className="h-4 w-4" />
                            Lessons
                        </TabsTrigger>
                        <TabsTrigger value="simulations" className="gap-2">
                            <Target className="h-4 w-4" />
                            Simulations
                        </TabsTrigger>
                        <TabsTrigger value="simulation" className="gap-2" disabled={!activeSimulation}>
                            <MessageSquare className="h-4 w-4" />
                            Active Session
                        </TabsTrigger>
                    </TabsList>

                    {/* Lessons Tab */}
                    <TabsContent value="lessons" className="mt-6">
                        {!playbook ? (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <h3 className="font-semibold mb-2">No Playbook Available</h3>
                                    <p className="text-muted-foreground mb-4">
                                        The playbook for this skill hasn&apos;t been generated yet.
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-6 lg:grid-cols-4">
                                {/* Lesson Navigation */}
                                {!assignedLesson && <div className="lg:col-span-1">
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-sm">Lessons</CardTitle>
                                        </CardHeader>
                                        <CardContent className="p-2">
                                            <div className="space-y-1">
                                                {playbook.lessons.map((lesson, idx) => {
                                                    const lp = progress?.lesson_progress?.find(p => p.lesson_id === lesson.id);
                                                    const isCompleted = lp?.quiz_passed;
                                                    const isCurrent = idx === currentLessonIndex;
                                                    
                                                    return (
                                                        <button
                                                            key={lesson.id}
                                                            onClick={() => {
                                                                setCurrentLessonIndex(idx);
                                                                setShowQuiz(false);
                                                                setQuizAnswers({});
                                                                setQuizResult(null);
                                                            }}
                                                            className={`w-full flex items-center gap-2 p-3 rounded-lg text-left transition-colors ${
                                                                isCurrent ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'
                                                            }`}
                                                        >
                                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                                                isCompleted ? 'bg-emerald-500 text-white' : 
                                                                isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted'
                                                            }`}>
                                                                {isCompleted ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                                                            </div>
                                                            <span className="text-sm truncate flex-1">{lesson.title}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>}

                                {/* Lesson Content */}
                                <div className={assignedLesson ? 'lg:col-span-4' : 'lg:col-span-3'} data-testid="lesson-content-panel">
                                    {currentLesson && (
                                        <Card>
                                            <CardHeader>
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <CardTitle>{currentLesson.title}</CardTitle>
                                                        <CardDescription className="flex items-center gap-2 mt-1">
                                                            <Clock className="h-4 w-4" />
                                                            {currentLesson.duration_minutes} min
                                                        </CardDescription>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {/* Audio Summary Indicator */}
                                                        <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                                                            <Sparkles className="h-3 w-3" />
                                                            <span>Audio: Key Points Summary</span>
                                                        </div>
                                                        {currentLesson.voice_url ? (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (isPlaying) {
                                                                        audioRef.current?.pause();
                                                                        setIsPlaying(false);
                                                                    } else {
                                                                        if (audioRef.current) {
                                                                            audioRef.current.src = currentLesson.voice_url;
                                                                            audioRef.current.onerror = () => {
                                                                                toast.error('Audio failed to load. Regenerating...');
                                                                                setIsPlaying(false);
                                                                                // Auto-regenerate on error
                                                                                generateVoice(currentLesson.id);
                                                                            };
                                                                            audioRef.current.play()
                                                                                .then(() => setIsPlaying(true))
                                                                                .catch((err) => {
                                                                                    console.error('Play error:', err);
                                                                                    toast.error('Could not play audio. Click Listen again.');
                                                                                    setIsPlaying(false);
                                                                                });
                                                                        }
                                                                    }
                                                                }}
                                                                className="gap-1"
                                                                title="Plays a summary of key points"
                                                            >
                                                                {isPlaying ? <Pause className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                                                                {isPlaying ? 'Pause' : 'Listen'}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => generateVoice(currentLesson.id)}
                                                                disabled={generatingVoice}
                                                                className="gap-1"
                                                                title="Generates audio summary of key points"
                                                            >
                                                                {generatingVoice ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Volume2 className="h-4 w-4" />
                                                                )}
                                                                {generatingVoice ? 'Generating...' : 'Listen'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent>
                                                <audio ref={audioRef} onEnded={() => setIsPlaying(false)} className="hidden" />
                                                
                                                {/* Audio Info Banner - shown when playing or just generated */}
                                                {(isPlaying || generatingVoice) && (
                                                    <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 flex items-center gap-2">
                                                        <Volume2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                        <p className="text-sm text-blue-700 dark:text-blue-300">
                                                            <span className="font-medium">Audio Summary:</span> This narration covers the key points and essential concepts. Read the full content below for complete details.
                                                        </p>
                                                    </div>
                                                )}
                                                
                                                {!showQuiz ? (
                                                    <>
                                                        {/* Lesson Content */}
                                                        <div className="prose prose-sm dark:prose-invert max-w-none mb-6">
                                                            <div dangerouslySetInnerHTML={{ 
                                                                __html: DOMPurify.sanitize(currentLesson.content.replace(/\n/g, '<br/>')) 
                                                            }} />
                                                        </div>
                                                        
                                                        {/* Key Points */}
                                                        {currentLesson.key_points?.length > 0 && (
                                                            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 mb-6">
                                                                <h4 className="font-semibold mb-3 flex items-center gap-2">
                                                                    <Star className="h-4 w-4 text-primary" />
                                                                    Key Points
                                                                </h4>
                                                                <ul className="space-y-2">
                                                                    {currentLesson.key_points.map((point, idx) => (
                                                                        <li key={`keypoint-${idx}-${point.substring(0, 15)}`} className="flex items-start gap-2 text-sm">
                                                                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                                                            {point}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Navigation */}
                                                        <div className="flex items-center justify-between pt-4 border-t">
                                                            {assignedLesson ? <span /> : (
                                                            <Button
                                                                variant="outline"
                                                                onClick={prevLesson}
                                                                disabled={currentLessonIndex === 0}
                                                            >
                                                                <ChevronLeft className="h-4 w-4 mr-1" />
                                                                Previous
                                                            </Button>
                                                            )}
                                                            
                                                            {lessonProgress?.quiz_passed && !assignedLesson ? (
                                                                <Button onClick={nextLesson} disabled={currentLessonIndex === playbook.lessons.length - 1}>
                                                                    Next Lesson
                                                                    <ChevronRight className="h-4 w-4 ml-1" />
                                                                </Button>
                                                            ) : (
                                                                <Button onClick={() => {
                                                                    startLesson(currentLesson.id);
                                                                    setShowQuiz(true);
                                                                }}>
                                                                    Take Quiz
                                                                    <ChevronRight className="h-4 w-4 ml-1" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* Quiz */}
                                                        <div className="space-y-6">
                                                            <div className="flex items-center justify-between">
                                                                <h3 className="font-semibold">Quiz: {currentLesson.title}</h3>
                                                                <Badge variant="outline">
                                                                    {Object.keys(quizAnswers).length}/{currentLesson.quiz_questions?.length || 0} answered
                                                                </Badge>
                                                            </div>
                                                            
                                                            {/* Handle missing quiz questions */}
                                                            {!currentLesson.quiz_questions || currentLesson.quiz_questions.length === 0 ? (
                                                                <div className="text-center py-8">
                                                                    <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-3" />
                                                                    <h4 className="font-medium text-lg">No Quiz Questions Available</h4>
                                                                    <p className="text-muted-foreground mt-2">
                                                                        Quiz questions for this lesson are being generated or could not be loaded.
                                                                    </p>
                                                                    <Button 
                                                                        variant="outline" 
                                                                        className="mt-4"
                                                                        onClick={() => {
                                                                            setShowQuiz(false);
                                                                            fetchSkillData(true);
                                                                        }}
                                                                    >
                                                                        <RefreshCw className="h-4 w-4 mr-2" />
                                                                        Refresh & Try Again
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                            <>
                                                            {currentLesson.quiz_questions.map((q, idx) => (
                                                                <div key={q.id || `q-${idx}`} className="p-4 rounded-xl border">
                                                                    <p className="font-medium mb-3">
                                                                        {idx + 1}. {q.question}
                                                                    </p>
                                                                    <RadioGroup
                                                                        value={quizAnswers[idx]?.toString()}
                                                                        onValueChange={(val) => setQuizAnswers({
                                                                            ...quizAnswers,
                                                                            [idx]: parseInt(val)
                                                                        })}
                                                                    >
                                                                        {q.options.map((option, optIdx) => (
                                                                            <div key={optIdx} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50">
                                                                                <RadioGroupItem value={optIdx.toString()} id={`q${idx}-o${optIdx}`} />
                                                                                <Label htmlFor={`q${idx}-o${optIdx}`} className="flex-1 cursor-pointer">
                                                                                    {option}
                                                                                </Label>
                                                                            </div>
                                                                        ))}
                                                                    </RadioGroup>
                                                                    
                                                                    {quizResult && quizResult.results && quizResult.results[idx] && (
                                                                        <div className={`mt-3 p-3 rounded-lg ${
                                                                            quizResult.results[idx]?.is_correct 
                                                                                ? 'bg-emerald-500/10 border border-emerald-500/30'
                                                                                : 'bg-red-500/10 border border-red-500/30'
                                                                        }`}>
                                                                            <div className="flex items-center gap-2 mb-1">
                                                                                {quizResult.results[idx]?.is_correct ? (
                                                                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                                                ) : (
                                                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                                                )}
                                                                                <span className="font-medium text-sm">
                                                                                    {quizResult.results[idx]?.is_correct ? 'Correct!' : 'Incorrect'}
                                                                                </span>
                                                                            </div>
                                                                            {!quizResult.results[idx]?.is_correct && (
                                                                                <p className="text-sm text-muted-foreground">
                                                                                    Correct answer: {quizResult.results[idx]?.correct_answer}
                                                                                </p>
                                                                            )}
                                                                            <p className="text-sm mt-1">{quizResult.results[idx]?.explanation}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            
                                                            {/* Quiz Actions */}
                                                            <QualityTaskOutcome kind="job_simulation" refKey={quizResult?.score !== undefined ? `${skillId}:${currentLesson.id}` : null}
                                                                attemptScore={quizResult?.score} onRetake={() => { setQuizAnswers({}); setQuizResult(null); }}>
                                                            <div className="flex items-center justify-between pt-4 border-t">
                                                                <Button variant="outline" onClick={() => setShowQuiz(false)}>
                                                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                                                    Back to Lesson
                                                                </Button>
                                                                
                                                                {quizResult ? (
                                                                    quizResult.passed && assignedLesson ? <span /> : quizResult.passed ? (
                                                                        <Button onClick={nextLesson}>
                                                                            Next Lesson
                                                                            <ChevronRight className="h-4 w-4 ml-1" />
                                                                        </Button>
                                                                    ) : quizResult.maxAttemptsReached ? (
                                                                        <Button onClick={async () => {
                                                                            // Request new questions from backend
                                                                            setSubmittingQuiz(true);
                                                                            try {
                                                                                await api.post(`/job-simulations/skills/${skillId}/lessons/${currentLesson.id}/regenerate-quiz`);
                                                                                await fetchSkillData(true);
                                                                                setQuizAnswers({});
                                                                                setQuizResult(null);
                                                                                toast.success('New questions generated!');
                                                                            } catch (error) {
                                                                                toast.error('Failed to generate new questions. Please try again.');
                                                                            } finally {
                                                                                setSubmittingQuiz(false);
                                                                            }
                                                                        }} disabled={submittingQuiz}>
                                                                            {submittingQuiz ? (
                                                                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                                                            ) : (
                                                                                <RefreshCw className="h-4 w-4 mr-1" />
                                                                            )}
                                                                            Get New Questions
                                                                        </Button>
                                                                    ) : (
                                                                        <Button onClick={() => {
                                                                            setQuizAnswers({});
                                                                            setQuizResult(null);
                                                                        }}>
                                                                            <RefreshCw className="h-4 w-4 mr-1" />
                                                                            Retry Quiz
                                                                        </Button>
                                                                    )
                                                                ) : (
                                                                    <Button 
                                                                        onClick={submitQuiz}
                                                                        disabled={submittingQuiz}
                                                                    >
                                                                        {submittingQuiz && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                                                                        Submit Quiz
                                                                    </Button>
                                                                )}
                                                            </div>
                                                            </QualityTaskOutcome>
                                                            </>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </CardContent>
                                        </Card>
                                    )}
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    {/* Simulations Tab */}
                    <TabsContent value="simulations" className="mt-6">
                        <div className="grid gap-6 md:grid-cols-3">
                            {/* Job Analysis */}
                            <Card className={!allLessonsCompleted ? 'opacity-60' : ''}>
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 rounded-xl bg-blue-500/10">
                                            <FileText className="h-6 w-6 text-blue-500" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Job Analysis</CardTitle>
                                            <CardDescription>Analyze job requirements</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Practice understanding and explaining {skill.name} job requirements with a hiring manager.
                                    </p>
                                    {progress?.job_analysis_completed ? (
                                        <CompletedStep
                                            score={progress.job_analysis_score}
                                            lastScore={progress.job_analysis_last_score}
                                            attempts={progress.job_analysis_attempts}
                                            disabled={simLoading}
                                            onRetake={() => handleStartSimulation('job_analysis')}
                                            testId="job-analysis"
                                        />
                                    ) : (
                                        <Button 
                                            onClick={() => handleStartSimulation('job_analysis')}
                                            disabled={!allLessonsCompleted || simLoading}
                                            className="w-full"
                                            data-testid="start-job-analysis-btn"
                                        >
                                            {!allLessonsCompleted ? (
                                                <>
                                                    <Lock className="h-4 w-4 mr-2" />
                                                    Complete Lessons First
                                                </>
                                            ) : simLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Play className="h-4 w-4 mr-2" />
                                                    Start Simulation
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Candidate Screening */}
                            <Card className={!progress?.job_analysis_completed ? 'opacity-60' : ''}>
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 rounded-xl bg-amber-500/10">
                                            <Users className="h-6 w-6 text-amber-500" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Candidate Screening</CardTitle>
                                            <CardDescription>Interview a candidate</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Conduct a mock phone screening with a {skill.name} candidate.
                                    </p>
                                    {progress?.candidate_screening_completed ? (
                                        <CompletedStep
                                            score={progress.candidate_screening_score}
                                            lastScore={progress.candidate_screening_last_score}
                                            attempts={progress.candidate_screening_attempts}
                                            disabled={simLoading}
                                            onRetake={() => handleStartSimulation('candidate_screening')}
                                            testId="screening"
                                        />
                                    ) : (
                                        <Button 
                                            onClick={() => handleStartSimulation('candidate_screening')}
                                            disabled={!progress?.job_analysis_completed || simLoading}
                                            className="w-full"
                                            data-testid="start-screening-btn"
                                        >
                                            {!progress?.job_analysis_completed ? (
                                                <>
                                                    <Lock className="h-4 w-4 mr-2" />
                                                    Complete Job Analysis First
                                                </>
                                            ) : simLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Play className="h-4 w-4 mr-2" />
                                                    Start Screening
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Final Assessment */}
                            <Card className={!progress?.candidate_screening_completed ? 'opacity-60' : ''}>
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 rounded-xl bg-emerald-500/10">
                                            <Award className="h-6 w-6 text-emerald-500" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Final Assessment</CardTitle>
                                            <CardDescription>Make your verdict</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Decide if the candidate is a good fit and justify your decision.
                                    </p>
                                    
                                    {/* Latest final assessment result: which job, score, status */}
                                    {progress?.final_assessment_score != null && (
                                        <div className="mb-4">
                                            <ResultSummary
                                                testId="final-assessment-summary"
                                                path={['Job Simulations', skill.name]}
                                                title="Final Assessment"
                                                score={progress.final_assessment_score}
                                                scoreLabel={progress.passed ? 'Final score' : 'Latest score'}
                                                status={progress.passed ? { label: 'Passed', tone: 'passed' } : { label: 'Not passed yet', tone: 'failed' }}
                                                details={[
                                                    { label: 'Attempts', value: progress.final_assessment_attempts || null },
                                                    { label: 'Pass mark', value: `${PASSING_SCORE}%` },
                                                    { label: 'Overall job score', value: progress.passed && progress.overall_score ? `${progress.overall_score}%` : null },
                                                ]}
                                            />
                                        </div>
                                    )}

                                    {/* Retakes are unlimited */}
                                    {progress?.candidate_screening_completed && !progress?.passed && progress?.retry_count > 0 && (
                                        <div className="mb-4 p-3 rounded-lg border bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400" data-testid="final-attempts-note">
                                            <div className="flex items-center gap-2">
                                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                                <span className="text-sm font-medium">
                                                    You can retry as many times as you need.
                                                </span>
                                            </div>
                                            <p className="text-xs mt-1 opacity-80">
                                                Score {PASSING_SCORE}% or higher to pass and advance to the next job.
                                            </p>
                                        </div>
                                    )}

                                    {/* Already Passed Badge */}
                                    {progress?.passed && !progress?.final_assessment_completed && (
                                        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                                                <Trophy className="h-4 w-4" />
                                                <span className="text-sm font-medium">Already passed! Move to the next job.</span>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {progress?.final_assessment_completed ? (
                                        progress.final_assessment_score == null && (
                                            <div className="flex items-center gap-2 text-emerald-600">
                                                <CheckCircle className="h-5 w-5" />
                                                <span>Completed</span>
                                            </div>
                                        )
                                    ) : (
                                        <Button 
                                            onClick={() => handleStartSimulation('final_assessment')}
                                            disabled={
                                                !progress?.candidate_screening_completed || 
                                                simLoading || 
                                                progress?.passed
                                            }
                                            className="w-full"
                                            data-testid="start-final-assessment-btn"
                                        >
                                            {!progress?.candidate_screening_completed ? (
                                                <>
                                                    <Lock className="h-4 w-4 mr-2" />
                                                    Complete Screening First
                                                </>
                                            ) : progress?.passed ? (
                                                <>
                                                    <CheckCircle className="h-4 w-4 mr-2" />
                                                    Already Passed
                                                </>
                                            ) : simLoading ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Play className="h-4 w-4 mr-2" />
                                                    {progress?.retry_count > 0 ? 'Retry Assessment' : 'Start Assessment'}
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Active Simulation Tab - VOICE ONLY MODE */}
                    <TabsContent value="simulation" className="mt-6">
                        {activeSimulation && (
                            <div className="grid lg:grid-cols-3 gap-6">
                                {/* Job Description Panel - Visible while speaking */}
                                <div className="lg:col-span-1 order-2 lg:order-1">
                                    <Card className="sticky top-4">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <Briefcase className="h-4 w-4 text-primary" />
                                                Job Description
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ScrollArea className="h-[320px]">
                                                {activeSimulation.context?.job_description ? (
                                                    <div className="prose prose-sm dark:prose-invert">
                                                        <p className="text-sm whitespace-pre-wrap">
                                                            {activeSimulation.context.job_description}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">
                                                        Job description will appear here once the simulation starts.
                                                    </p>
                                                )}
                                            </ScrollArea>
                                            {activeSimulation.context?.candidate_profile && (
                                                <div className="mt-4 pt-4 border-t">
                                                    <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                                        <Users className="h-4 w-4 text-amber-500" />
                                                        Candidate Profile
                                                    </h4>
                                                    <ScrollArea className="h-[150px]">
                                                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                                            {activeSimulation.context.candidate_profile}
                                                        </p>
                                                    </ScrollArea>
                                                </div>
                                            )}
                                        </CardContent>
                                        
                                        {/* Naturalness Warning Banner */}
                                        {naturalnessWarning && (
                                            <div className="border-t px-4 py-3 bg-amber-50 dark:bg-amber-950/30">
                                                <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
                                                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                    <div className="text-xs">
                                                        <p className="font-medium">Response Flagged</p>
                                                        <p className="text-amber-500 dark:text-amber-500">
                                                            {naturalnessWarning.message || 'Please speak naturally from your own knowledge.'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                </div>
                                
                                {/* Main Conversation Area */}
                                <div className="lg:col-span-2 order-1 lg:order-2">
                            <Card>
                                <CardHeader className="border-b">
                                    <div className="flex items-center justify-between flex-wrap gap-4">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                {activeSimulation.simulation_type === 'job_analysis' && <FileText className="h-5 w-5 text-blue-500" />}
                                                {activeSimulation.simulation_type === 'candidate_screening' && <Users className="h-5 w-5 text-amber-500" />}
                                                {activeSimulation.simulation_type === 'final_assessment' && <Award className="h-5 w-5 text-emerald-500" />}
                                                {activeSimulation.simulation_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </CardTitle>
                                            <CardDescription>
                                                {skill.name} • Difficulty: {activeSimulation.difficulty}
                                            </CardDescription>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-primary border-primary">
                                                <Mic className="h-3 w-3 mr-1" />
                                                Voice Only
                                            </Badge>
                                            {activeSimulation.simulation_type === 'final_assessment' && (
                                                <Badge variant="outline" className={userExchanges >= FINAL_ASSESSMENT_VERDICT_AT ? 'text-amber-500 border-amber-500' : ''} data-testid="final-exchange-count">
                                                    {userExchanges} exchange{userExchanges === 1 ? '' : 's'}{userExchanges < FINAL_ASSESSMENT_VERDICT_AT ? ` · verdict after ${FINAL_ASSESSMENT_VERDICT_AT}` : ''}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {/* Hidden audio element for TTS */}
                                    <audio ref={ttsAudioRef} className="hidden" />
                                    
                                    {/* Messages */}
                                    <ScrollArea className="h-[400px] p-4">
                                        <div className="space-y-4">
                                            {simMessages.map((msg, idx) => (
                                                <div 
                                                    key={`sim-msg-${idx}-${msg.role}`}
                                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div className={`max-w-[80%] p-3 rounded-xl ${
                                                        msg.role === 'user' 
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'bg-muted'
                                                    }`}>
                                                        {msg.isVoice && (
                                                            <Badge variant="outline" className="mb-1 text-xs bg-background/50">
                                                                <Mic className="h-3 w-3 mr-1" /> Voice
                                                            </Badge>
                                                        )}
                                                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {simLoading && (
                                                <div className="flex justify-start">
                                                    <div className="bg-muted p-3 rounded-xl">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    </div>
                                                </div>
                                            )}
                                            <div ref={messagesEndRef} />
                                        </div>
                                    </ScrollArea>

                                    {/* Voice Waveform when recording */}
                                    {isRecording && (
                                        <div className="px-4 pb-2">
                                            <VoiceWaveform isRecording={isRecording} audioLevel={audioLevel} />
                                        </div>
                                    )}
                                    
                                    {/* Voice Input Controls */}
                                    <div className="border-t p-4">
                                        {/* Speaking Indicator */}
                                        {isSpeaking && (
                                            <div className="mb-3 p-3 rounded-lg bg-primary/10 border border-primary/30 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Volume2 className="h-5 w-5 text-primary animate-pulse" />
                                                    <span className="text-sm font-medium">AI is speaking...</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">Wait for the AI to finish or click speaker to stop</p>
                                            </div>
                                        )}

                                        {/* Final Assessment Verdict Section */}
                                        {activeSimulation.simulation_type === 'final_assessment' && shouldPromptVerdict && (
                                            <div className="space-y-4 mb-6" data-testid="verdict-section">
                                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                                                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                                                        <AlertCircle className="h-4 w-4" />
                                                        <span className="text-sm font-medium" data-testid="verdict-banner">
                                                            {verdictRequired
                                                                ? 'This is the longest a final discussion can run. Submit your verdict now.'
                                                                : `Ready to decide? Submit your verdict below, or keep talking with the hiring manager (up to ${FINAL_ASSESSMENT_MAX_EXCHANGES} exchanges).`}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <p className="text-sm font-medium">Your Final Verdict:</p>
                                                <div className="flex gap-4">
                                                    <Button
                                                        variant={selectedVerdict === 'good_fit' ? 'default' : 'outline'}
                                                        onClick={() => setSelectedVerdict('good_fit')}
                                                        className="flex-1"
                                                        data-testid="verdict-good-fit"
                                                    >
                                                        <CheckCircle className="h-4 w-4 mr-2" />
                                                        Good Fit
                                                    </Button>
                                                    <Button
                                                        variant={selectedVerdict === 'not_good_fit' ? 'destructive' : 'outline'}
                                                        onClick={() => setSelectedVerdict('not_good_fit')}
                                                        className="flex-1"
                                                        data-testid="verdict-not-good-fit"
                                                    >
                                                        <XCircle className="h-4 w-4 mr-2" />
                                                        Not a Good Fit
                                                    </Button>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Explain your reasoning (speak your reasoning then type key points):</Label>
                                                    <textarea
                                                        placeholder="Summary of your reasoning..."
                                                        value={verdictReasoning}
                                                        onChange={(e) => setVerdictReasoning(e.target.value)}
                                                        rows={3}
                                                        className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                    />
                                                </div>
                                                <Button 
                                                    onClick={submitVerdict}
                                                    disabled={!selectedVerdict || !verdictReasoning.trim() || simCompleting}
                                                    className="w-full bg-gradient-to-r from-primary to-primary/80"
                                                    data-testid="submit-verdict-btn"
                                                >
                                                    {simCompleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Award className="h-4 w-4 mr-2" />}
                                                    Submit Final Verdict
                                                </Button>
                                                <p className="text-xs text-muted-foreground text-center">
                                                    Score {PASSING_SCORE}% or higher to pass and advance to the next job
                                                </p>
                                            </div>
                                        )}
                                        {!verdictRequired && (
                                            <div className="flex flex-col items-center gap-4">
                                                {/* Voice Controls - Large prominent buttons */}
                                                <div className="flex items-center gap-4">
                                                    {/* Main Mic Button */}
                                                    <Button
                                                        size="lg"
                                                        variant={isRecording ? 'destructive' : 'default'}
                                                        onClick={isRecording ? stopRecording : startRecording}
                                                        disabled={simLoading || verdictRequired}
                                                        className={`
                                                            h-16 w-16 rounded-full transition-all
                                                            ${isRecording 
                                                                ? 'bg-destructive hover:bg-destructive/90 animate-pulse' 
                                                                : 'bg-primary hover:bg-primary/90'
                                                            }
                                                        `}
                                                        data-testid="voice-btn"
                                                    >
                                                        {isRecording ? (
                                                            <MicOff className="h-8 w-8" />
                                                        ) : (
                                                            <Mic className="h-8 w-8" />
                                                        )}
                                                    </Button>
                                                    
                                                    {/* Speaker/Mute Button */}
                                                    <Button
                                                        size="lg"
                                                        variant="outline"
                                                        onClick={isSpeaking ? stopSpeaking : toggleMute}
                                                        className="h-16 w-16 rounded-full"
                                                        data-testid="speaker-btn"
                                                    >
                                                        {isSpeaking ? (
                                                            <VolumeX className="h-8 w-8 text-destructive" />
                                                        ) : isMuted ? (
                                                            <VolumeX className="h-8 w-8 text-muted-foreground" />
                                                        ) : (
                                                            <Volume2 className="h-8 w-8 text-primary" />
                                                        )}
                                                    </Button>
                                                </div>
                                                
                                                {/* Instructions */}
                                                <p className="text-sm text-muted-foreground text-center">
                                                    {isRecording 
                                                        ? 'Recording... Click the mic button when done speaking'
                                                        : simLoading 
                                                            ? 'Processing your voice...'
                                                            : 'Click the mic button to speak your response'
                                                    }
                                                </p>
                                                
                                                {/* Mute status */}
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <span>AI Voice: {isMuted ? 'Off' : 'On'}</span>
                                                </div>
                                                
                                                {/* Complete button for non-final-assessment simulations */}
                                                {activeSimulation.simulation_type !== 'final_assessment' && 
                                                 simMessages.filter(m => m.role === 'user').length >= 3 && (
                                                    <Button
                                                        onClick={completeSimulation}
                                                        disabled={simLoading || isRecording || simCompleting}
                                                        className="bg-gradient-to-r from-primary to-primary/80"
                                                        data-testid="complete-simulation-btn"
                                                    >
                                                        {simCompleting ? (
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                        ) : (
                                                            <CheckCircle className="h-4 w-4 mr-2" />
                                                        )}
                                                        Complete Simulation
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
                
                {/* Session Guidance Modal - Pre-session Do's and Don'ts */}
                <SessionGuidanceModal
                    open={showGuidanceModal}
                    onClose={() => {
                        setShowGuidanceModal(false);
                        setPendingSimulationType(null);
                    }}
                    onStart={() => {
                        setShowGuidanceModal(false);
                        if (pendingSimulationType) {
                            startSimulation(pendingSimulationType);
                            setPendingSimulationType(null);
                        }
                    }}
                    phase={pendingSimulationType === 'job_analysis' ? 'phase5_job_analysis' : 
                           pendingSimulationType === 'candidate_screening' ? 'phase5_screening' :
                           pendingSimulationType === 'final_assessment' ? 'phase5_final_assessment' :
                           'phase5_job_analysis'}
                />
                <FinalResultDialog result={finalResult} onClose={() => setFinalResult(null)} />
            </div>
        </MainLayout>
    );
};

export default SkillLearning;
